import React from 'react';
import { motion } from 'framer-motion';
import ContentPage from '../ContentPage';

const PrivacyPolicy: React.FC = () => {
  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  return (
    <ContentPage title="Privacy Policy" accent="teal">
      {/* Decorative elements */}
      <div className="relative mb-8">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-teal-500/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/10 rounded-full blur-lg"></div>
        
        <p className="text-lg mb-6 relative z-10">
          At SokoVibe, we value your privacy and are committed to protecting your personal information.
          This policy outlines how we collect, use, and safeguard your data.
        </p>
      </div>
      
      <motion.div 
        className="prose prose-sm max-w-none text-gray-700"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.p variants={itemVariants} className="font-medium text-teal-700 bg-teal-50 inline-block px-3 py-1 rounded-md">Last Updated: July 9, 2025</motion.p>
        
        <motion.p variants={itemVariants} className="mt-4">
          At SokoVibe, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our marketplace services.
        </motion.p>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-teal-600">Information We Collect</motion.h3>
        <motion.p variants={itemVariants}>We may collect information about you in various ways, including:</motion.p>
        <motion.div variants={itemVariants}>
          <ul className="list-none pl-0 space-y-3 mb-4 mt-3">
            <li className="pl-4 border-l-2 border-purple-300 bg-gradient-to-r from-purple-50 to-transparent p-2 rounded-r-md">
              <strong className="text-purple-700">Personal Data:</strong> Name, email address, phone number, billing address, shipping address, payment information, and other information you provide when creating an account or making a purchase.
            </li>
            <li className="pl-4 border-l-2 border-teal-300 bg-gradient-to-r from-teal-50 to-transparent p-2 rounded-r-md">
              <strong className="text-teal-700">Usage Data:</strong> Information about how you interact with our website, such as pages visited, time spent, and actions taken.
            </li>
            <li className="pl-4 border-l-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-transparent p-2 rounded-r-md">
              <strong className="text-yellow-700">Device Data:</strong> Information about your device, including IP address, browser type, operating system, and device identifiers.
            </li>
          </ul>
        </motion.div>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-700">How We Use Your Information</motion.h3>
        <motion.p variants={itemVariants}>We may use the information we collect for various purposes, including to:</motion.p>
        <motion.ul variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 mb-4 pl-0 list-none">
          <li className="flex items-center p-2 bg-gradient-to-r from-purple-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
            Provide, maintain, and improve our services
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-teal-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-teal-500 rounded-full mr-2"></span>
            Process transactions and send related information
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-yellow-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
            Send administrative information and alerts
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-orange-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            Respond to your comments and requests
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-purple-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
            Personalize your experience
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-teal-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-teal-500 rounded-full mr-2"></span>
            Monitor and analyze trends and usage
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-yellow-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
            Detect and prevent fraudulent transactions
          </li>
          <li className="flex items-center p-2 bg-gradient-to-r from-orange-50 to-transparent rounded-md">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            Comply with legal obligations
          </li>
        </motion.ul>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-teal-600">Disclosure of Your Information</motion.h3>
        <p>We may share your information with:</p>
        <ul className="list-disc pl-5 space-y-1 mb-4">
          <li><strong>Service Providers:</strong> Third parties that perform services on our behalf, such as payment processing, data analysis, email delivery, and hosting services.</li>
          <li><strong>Business Partners:</strong> When necessary to provide you with services you request.</li>
          <li><strong>Legal Requirements:</strong> When required by law or in response to legal process.</li>
          <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business.</li>
        </ul>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-700">Your Choices</motion.h3>
        <p>You have certain choices regarding the information we collect and how it is used:</p>
        <ul className="list-disc pl-5 space-y-1 mb-4">
          <li><strong>Account Information:</strong> You may update or delete your account information at any time by logging into your account.</li>
          <li><strong>Marketing Communications:</strong> You may opt out of receiving promotional emails by following the instructions in those emails.</li>
          <li><strong>Cookies:</strong> You can set your browser to refuse all or some browser cookies or to alert you when cookies are being sent.</li>
        </ul>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-teal-600">Data Security</motion.h3>
        <p>
          We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration, and disclosure. However, no method of transmission over the Internet or electronic storage is 100% secure.
        </p>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-700">Changes to Our Privacy Policy</motion.h3>
        <p>
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
        </p>
        
        <motion.h3 variants={itemVariants} className="text-lg font-semibold mt-6 mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-teal-600">Contact Us</motion.h3>
        <motion.p variants={itemVariants}>
          If you have any questions about this Privacy Policy, please contact us at:
        </motion.p>
        <motion.div 
          variants={itemVariants}
          className="mt-2 p-4 bg-gradient-to-r from-purple-50 to-teal-50 rounded-lg border border-teal-100 flex items-center"
        >
          <svg className="w-6 h-6 mr-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-teal-600">
            privacy@sokovibe.com
          </span>
        </motion.div>
      </motion.div>
      
      {/* Security badge section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-12 p-6 bg-gradient-to-r from-teal-50 via-purple-50 to-teal-50 rounded-xl border border-teal-100 flex flex-col sm:flex-row items-center justify-center gap-6"
      >
        <div className="flex items-center justify-center bg-white p-3 rounded-full shadow-sm">
          <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <div>
          <h4 className="text-lg font-semibold mb-1 text-teal-700">Your Data is Protected</h4>
          <p className="text-sm text-gray-700">SokoVibe uses industry-standard security measures to keep your information safe and secure.</p>
        </div>
      </motion.div>
    </ContentPage>
  );
};

export default PrivacyPolicy;
